import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { colors } from '@/constants/colors';
import { setLanIsolationApi, type ApiVendoDevice } from '@/api/vendo';
import { MIKROTIK_LAN_PORTS, MIKROTIK_TITLE } from '@/components/Admin/Prompt/MikrotikWiring';

/**
 * Which MikroTik port is for what — and the operator can change it.
 *
 * Every MikroTik model (hEX, RB4011, CCR2004, CCR2116) splits its LAN ports
 * into Customer ports (phones / a plain access point, untagged) and Trunk
 * ports (a managed switch or VLAN access point carrying the sub-vendos,
 * tagged). The router already runs that split; the operator could neither see
 * it nor change it, so a CCR owner had to ask "how do I trunk?" (Ray
 * 2026-10-21). Default = the first two LAN ports Customer, the rest Trunk;
 * the internet port and the owner port are fixed here.
 */
export type PortRole = 'access' | 'trunk';

type RouterLike = {
   model?: string | null;
   wanPort?: string | null;
   ownerPort?: string | null;
   lanIsolation?: boolean | null;
   portRoles?: { port: string; role: PortRole }[] | null;
   connected?: boolean | null;
};

/** The roles the router runs (or will run once sub-vendo VLANs are on): same rule as the server's resolvePortRoles. */
export function mikrotikPortRoles(router: RouterLike | null | undefined): { port: string; role: PortRole }[] {
   const lan = MIKROTIK_LAN_PORTS[router?.model ?? ''] ?? [];
   const wan = router?.wanPort || 'ether1';
   const owner = router?.ownerPort || null;
   const chosen = new Map((router?.portRoles ?? []).map((o) => [o.port, o.role] as const));
   return lan
      .filter((p) => p !== wan && p !== owner)
      .map((port, i) => ({ port, role: chosen.get(port) ?? (i < 2 ? 'access' : 'trunk') }));
}


/** One port drawn as the real socket: an RJ45 opening with its clip notch and two lights, or an SFP+ cage slot. */
const Jack: React.FC<{ port: string; tone: { bg: string; line: string; ink: string } }> = ({ port, tone }) => {
   const sfp = port.startsWith('sfp');
   return (
      <svg viewBox="0 0 44 34" width="100%" height="34" aria-hidden style={{ display: 'block' }}>
         <rect x="1" y="1" width="42" height="32" rx="4" fill={tone.bg} stroke={tone.line} strokeWidth="1.5" />
         {sfp ? (
            <>
               <rect x="6" y="11" width="32" height="13" rx="1.5" fill="#1f2937" />
               <rect x="9" y="14" width="26" height="2" rx="1" fill="#4b5563" />
            </>
         ) : (
            <>
               {/* RJ45 socket: a tall opening with the latch notch at the bottom */}
               <path d="M10 8 H34 V22 H27 V27 H17 V22 H10 Z" fill="#1f2937" />
               {[13, 16, 19, 22, 25, 28, 31].map((x) => (
                  <rect key={x} x={x - 0.6} y="9.5" width="1.2" height="4" fill="#d4af37" />
               ))}
               <rect x="4" y="4" width="4" height="2.5" rx="0.8" fill="#22c55e" />
               <rect x="36" y="4" width="4" height="2.5" rx="0.8" fill="#f59e0b" />
            </>
         )}
      </svg>
   );
};

const short = (p: string) => p.replace('ether', 'ether ').replace('sfp-sfpplus', 'SFP+ ');

const MikrotikPortRolesCard: React.FC<{
   deviceId: string;
   router: RouterLike | null | undefined;
   onSaved?: (device: ApiVendoDevice) => void;
}> = ({ deviceId, router, onSaved }) => {
   const current = useMemo(() => mikrotikPortRoles(router), [router]);
   const [draft, setDraft] = useState(current);
   const [busy, setBusy] = useState(false);
   const [confirming, setConfirming] = useState(false);
   useEffect(() => {
      setDraft(current);
      setConfirming(false);
   }, [current]);

   if (!current.length) return null;
   const isolated = router?.lanIsolation === true;
   const wan = router?.wanPort || 'ether1';
   const owner = router?.ownerPort || null;
   const changed = draft.filter((d) => current.find((c) => c.port === d.port)?.role !== d.role);
   const toTrunk = changed.filter((c) => c.role === 'trunk').map((c) => short(c.port));
   const flip = (port: string) => {
      if (busy) return;
      setConfirming(false);
      setDraft((ds) => ds.map((d) => (d.port === port ? { ...d, role: d.role === 'access' ? 'trunk' : 'access' } : d)));
   };
   const save = async () => {
      if (!changed.length || busy) return;
      setBusy(true);
      try {
         const r = await setLanIsolationApi(deviceId, {
            lanIsolation: isolated,
            portRoles: draft.map((d) => ({ port: d.port, role: d.role })),
         });
         toast.success(isolated ? 'Ports saved. Sending to the router now.' : 'Ports saved. They switch on with Access Point VLAN or your first sub-vendo.', { autoClose: 5000 });
         onSaved?.(r.device);
         setConfirming(false);
      } catch (err: any) {
         toast.error(err?.response?.data?.error || 'Could not save the ports');
      } finally {
         setBusy(false);
      }
   };

   return (
      <Card data-testid="mikrotik-port-roles">
         <Head>
            <Title>Ports</Title>
            <Model>{MIKROTIK_TITLE[router?.model ?? ''] ?? 'MikroTik'}</Model>
         </Head>
         <Legend>
            <Key $role="access">Customer</Key> phones or a plain access point ·{' '}
            <Key $role="trunk">Trunk</Key> managed switch or VLAN access point for sub-vendos.
         </Legend>
         <Hint>
            <span aria-hidden>👆</span> Tap a port to switch it between Customer and Trunk.
         </Hint>
         <Grid>
            <Fixed title="Internet">
               <Jack port={wan} tone={FIXED_TONE} />
               <PortName>{short(wan)}</PortName>
               <Role>Internet</Role>
            </Fixed>
            {draft.map((d) => {
               const moved = current.find((c) => c.port === d.port)?.role !== d.role;
               return (
                  <Port
                     key={d.port}
                     type="button"
                     $role={d.role}
                     $moved={moved}
                     aria-label={`${d.port}: ${d.role === 'access' ? 'Customer' : 'Trunk'}`}
                     onClick={() => flip(d.port)}
                     disabled={busy}
                  >
                     <Jack port={d.port} tone={ROLE[d.role]} />
                     <PortName>{short(d.port)}</PortName>
                     <Role>{d.role === 'access' ? 'Customer' : 'Trunk'}</Role>
                     <Swap aria-hidden>⇄ {moved ? 'changed' : 'tap'}</Swap>
                  </Port>
               );
            })}
            {owner && (
               <Fixed title="Owner">
                  <Jack port={owner} tone={OWNER_TONE} />
                  <PortName>{short(owner)}</PortName>
                  <Role>Owner</Role>
               </Fixed>
            )}
         </Grid>
         {!isolated && (
            <Note>Right now every port serves customers. These roles switch on with your first sub-vendo.</Note>
         )}
         {changed.length > 0 &&
            (confirming ? (
               <Confirm>
                  <span>
                     {toTrunk.length
                        ? `Phones plugged straight into ${toTrunk.join(', ')} lose internet until moved to a Customer port.`
                        : 'The router updates its ports now.'}{' '}
                     Save?
                  </span>
                  <Btns>
                     <Ghost type="button" onClick={() => setConfirming(false)} disabled={busy}>Cancel</Ghost>
                     <Primary type="button" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Yes, save'}</Primary>
                  </Btns>
               </Confirm>
            ) : (
               <Bar>
                  <span>{changed.length} port{changed.length > 1 ? 's' : ''} changed</span>
                  <Btns>
                     <Ghost type="button" onClick={() => setDraft(current)}>Undo</Ghost>
                     <Primary type="button" onClick={() => (isolated ? setConfirming(true) : void save())}>Save ports</Primary>
                  </Btns>
               </Bar>
            ))}
      </Card>
   );
};

export default MikrotikPortRolesCard;

const ROLE = {
   access: { bg: '#dcfce7', line: '#16a34a', ink: '#166534' },
   trunk: { bg: '#dbeafe', line: '#2563eb', ink: '#1e40af' },
};
const FIXED_TONE = { bg: '#e5e7eb', line: '#6b7280', ink: '#374151' };
const OWNER_TONE = { bg: '#ffedd5', line: '#ea580c', ink: '#9a3412' };

const Card = styled.div`
   background: var(--app-surface, ${colors.WHITE});
   border: 1px solid ${colors.GRAY12};
   border-radius: 12px;
   padding: 12px 14px;
   display: flex;
   flex-direction: column;
   gap: 8px;
`;
const Head = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
`;
const Title = styled.h3`
   font-size: 14px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
   margin: 0;
`;
const Model = styled.span`
   font-size: 10px;
   font-weight: 700;
   padding: 2px 8px;
   border-radius: 999px;
   background: ${colors.PRIMARY}14;
   color: ${colors.PRIMARY};
   text-transform: uppercase;
   letter-spacing: 0.04em;
`;
const Legend = styled.p`
   font-size: 12px;
   color: ${colors.GRAY3};
   margin: 0;
   line-height: 1.5;
`;
const Key = styled.span<{ $role: PortRole }>`
   font-weight: 700;
   color: ${({ $role }) => ROLE[$role].ink};
`;
const Grid = styled.div`
   display: grid;
   grid-template-columns: repeat(4, minmax(0, 1fr));
   gap: 6px;
   @media (min-width: 640px) {
      grid-template-columns: repeat(8, minmax(0, 1fr));
   }
`;
const cell = `
   border-radius: 8px;
   padding: 6px 4px;
   display: flex;
   flex-direction: column;
   align-items: center;
   gap: 1px;
   min-width: 0;
`;
const Port = styled.button<{ $role: PortRole; $moved: boolean }>`
   ${cell}
   cursor: pointer;
   background: #f8fafc;
   color: ${({ $role }) => ROLE[$role].ink};
   border: ${({ $moved }) => ($moved ? 2 : 1)}px ${({ $moved }) => ($moved ? 'dashed' : 'solid')} ${({ $role }) => ROLE[$role].line};
   box-shadow: 0 2px 0 ${({ $role }) => ROLE[$role].line}55, 0 1px 3px rgba(0, 0, 0, 0.08);
   transition: transform 0.08s ease, box-shadow 0.08s ease;
   -webkit-tap-highlight-color: transparent;
   &:hover { transform: translateY(-1px); box-shadow: 0 3px 0 ${({ $role }) => ROLE[$role].line}66, 0 2px 6px rgba(0, 0, 0, 0.12); }
   &:active { transform: translateY(1px); box-shadow: 0 0 0 ${({ $role }) => ROLE[$role].line}00; }
   &:focus-visible { outline: 2px solid ${colors.PRIMARY}; outline-offset: 2px; }
   &:disabled { opacity: 0.6; cursor: default; transform: none; }
`;
const Fixed = styled.div`
   ${cell}
   background: #f3f4f6;
   color: #374151;
   border: 1px solid #d1d5db;
`;
const PortName = styled.span`
   font-size: 12px;
   font-weight: 700;
   white-space: nowrap;
`;
const Role = styled.span`
   font-size: 10px;
   font-weight: 600;
`;
const Hint = styled.div`
   display: flex;
   align-items: center;
   gap: 6px;
   font-size: 12px;
   font-weight: 600;
   color: ${colors.PRIMARY};
   background: ${colors.PRIMARY}0f;
   border-radius: 8px;
   padding: 6px 10px;
`;
const Swap = styled.span`
   font-size: 9px;
   font-weight: 700;
   opacity: 0.75;
   text-transform: uppercase;
   letter-spacing: 0.04em;
`;
const Note = styled.p`
   font-size: 12px;
   color: ${colors.GRAY3};
   margin: 0;
`;
const Bar = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 8px;
   font-size: 12px;
   font-weight: 600;
   color: ${colors.MAIN_DARK};
`;
const Confirm = styled(Bar)`
   background: #fff7e6;
   border: 1px solid #f5c56b;
   border-radius: 8px;
   padding: 8px 10px;
   color: #8a5a00;
   flex-wrap: wrap;
`;
const Btns = styled.div`
   display: flex;
   gap: 6px;
   flex: 0 0 auto;
`;
const Primary = styled.button`
   background: ${colors.PRIMARY};
   color: #fff;
   border: 0;
   border-radius: 8px;
   padding: 6px 12px;
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   &:disabled { opacity: 0.6; }
`;
const Ghost = styled.button`
   background: transparent;
   color: ${colors.GRAY3};
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   padding: 6px 10px;
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
`;
